// FormBuilderBasicOptions — the properties panel's "Basic options" accordion:
// Title (question label) + Hint text, each with an Aa styling toggle revealing
// a text-style card (font size · B/I/U · color · opacity), then Placeholder for
// the types that carry one — the shared basic rows in that order, BEFORE the
// type-specific extras. checkList's extras (choices/multiple/options) stay
// inline here; the other type-specific extras render via
// FormBuilderBasicExtras in the slot after Placeholder. Writes go straight into the
// selected element's basicProperties via onChange; keys are prefix-resolved
// (field* / section* / page*). Pages carry a title but no hint/placeholder, so
// the Hint block is gated on the type actually carrying a `${prefix}HintText`
// key.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton, Input, SelectDropdown, Switch, Textarea } from '@/components/common';
import {
  basicKeyPrefix,
  basicRecord,
  formElementDefaultProperties,
  type ChoiceOption,
  type ElementProperties,
  type FieldImageRef,
} from './utils/formElementDefaults';
import { joinHex8, splitHex8 } from './utils/builderColors';
import FormBuilderBasicExtras from './FormBuilderBasicExtras';
import ChoiceOptionsEditor from './ChoiceOptionsEditor';
import type { BindableField, BuilderField, BuilderPage } from './utils/builderModel';
import { useFieldImageUrl } from './utils/useFieldImageUrl';

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px'];

const STYLE_FLAGS = [
  { flag: 'bold', icon: 'bold', labelKey: 'forms_builder_props_bold' },
  { flag: 'italic', icon: 'italic', labelKey: 'forms_builder_props_italic' },
  { flag: 'underline', icon: 'underline', labelKey: 'forms_builder_props_underline' },
];

interface TextStyleValue {
  color?: string;
  size?: string;
  fontType?: string[];
}

interface TextStyleEditorProps {
  color: string;
  size: string;
  fontType: string[];
  /** The type's pristine defaults — "Default" selections write these back. */
  defaultColor: string;
  onChange: (patch: TextStyleValue) => void;
}

const TextStyleEditor = ({
  color,
  size,
  fontType,
  defaultColor,
  onChange,
}: TextStyleEditorProps) => {
  const { t } = useTranslation();
  const { rgb, alpha } = splitHex8(color);

  return (
    <div className='form-builder-props__style-card'>
      <div className='form-builder-props__style-row'>
        <span className='form-builder-props__style-label'>
          {t('forms_builder_props_font_size')}
        </span>
        <SelectDropdown
          size='sm'
          aria-label={t('forms_builder_props_font_size')}
          value={size}
          options={FONT_SIZES.map((s) => ({ value: s, label: s }))}
          onChange={(next) => onChange({ size: next })}
        />
      </div>

      <div className='form-builder-props__style-row'>
        <span className='form-builder-props__style-label'>{t('forms_builder_props_style')}</span>
        <div className='form-builder-props__style-btns'>
          {STYLE_FLAGS.map((f) => {
            const active = fontType.includes(f.flag);
            return (
              <button
                key={f.flag}
                type='button'
                className={[
                  'form-builder-props__style-btn',
                  active && 'form-builder-props__style-btn--active',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={t(f.labelKey)}
                aria-pressed={active}
                onClick={() =>
                  onChange({
                    fontType: active ? fontType.filter((x) => x !== f.flag) : [...fontType, f.flag],
                  })
                }
              >
                <Icon name={f.icon} size={14} />
              </button>
            );
          })}
        </div>
      </div>

      <div className='form-builder-props__style-row'>
        <span className='form-builder-props__style-label'>{t('forms_builder_props_color')}</span>
        <label className='form-builder-props__swatch'>
          <input
            type='color'
            value={rgb}
            aria-label={t('forms_builder_props_color')}
            onChange={(e) => onChange({ color: joinHex8(e.target.value, alpha) })}
          />
          <span
            className='form-builder-props__swatch-chip'
            style={{ background: color }}
            aria-hidden='true'
          />
          {color === defaultColor ? t('forms_builder_props_size_default') : rgb}
        </label>
      </div>

      <div className='form-builder-props__style-row'>
        <span className='form-builder-props__style-label'>{t('forms_builder_props_opacity')}</span>
        <input
          type='range'
          className='form-builder-props__opacity'
          min={0}
          max={100}
          value={alpha}
          aria-label={t('forms_builder_props_opacity')}
          onChange={(e) => onChange({ color: joinHex8(rgb, Number(e.target.value)) })}
        />
        <span className='form-builder-props__opacity-value'>{alpha}%</span>
      </div>
    </div>
  );
};

interface FormBuilderBasicOptionsProps {
  field: BuilderField | BuilderPage;
  onChange: (properties: ElementProperties) => void;
  /** A response set was applied to this checkList — replaces the optionList
   *  and kills the field's conditional-logic rules (see ChoiceOptionsEditor). */
  onApplyResponseSet?: (options: ChoiceOption[]) => void;
  /** Passed straight through to the dataSource extras. */
  bindableFields?: BindableField[];
}

const FormBuilderBasicOptions = ({
  field,
  onChange,
  onApplyResponseSet,
  bindableFields = [],
}: FormBuilderBasicOptionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [styleOpen, setStyleOpen] = useState<'title' | 'hint' | null>(null);

  const prefix = basicKeyPrefix(field.elementType);
  const bp = basicRecord(field.properties);
  const defaults = basicRecord(formElementDefaultProperties[field.elementType]);
  // Conditional rules lock multipleChoice (pages carry no rules key).
  const rulesLocked = 'rules' in field && field.rules.length > 0;
  // Pages carry a title but no hint — only render the Hint block when the type
  // actually defines one.
  const hasHint = `${prefix}HintText` in defaults;
  // The title's sub-note reads "Page name" for pages, "Question label" otherwise.
  const titleNoteKey =
    field.elementType === 'page'
      ? 'forms_builder_props_page_note'
      : 'forms_builder_props_title_note';

  const setBasic = (patch: Record<string, unknown>) =>
    onChange({
      ...field.properties,
      basicProperties: { ...(field.properties.basicProperties as object), ...patch },
    } as ElementProperties);

  // Shared hidden input for the reference-image picker (empty + replace both open it).
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Reference image: an object URL for a pending pick, otherwise resolved from
  // the saved media id. Top-level call — the image block below is conditional,
  // hooks can't be.
  const fieldImageUrl = useFieldImageUrl(
    bp.fieldImage as FieldImageRef | null | undefined,
    bp.fieldImageFile,
  );

  const str = (key: string) => (bp[key] as string) ?? '';

  // Style-editor bindings for the `${base}Color/Size/FontType` trio.
  const styleEditorProps = (base: string): TextStyleEditorProps => ({
    color: str(`${base}Color`) || '#000000ff',
    size: str(`${base}Size`) || '14px',
    fontType: (bp[`${base}FontType`] as string[]) ?? [],
    defaultColor: (defaults[`${base}Color`] as string) ?? '#000000ff',
    onChange: (patch) => {
      const mapped: Record<string, unknown> = {};
      if (patch.color !== undefined) mapped[`${base}Color`] = patch.color;
      if (patch.size !== undefined) mapped[`${base}Size`] = patch.size;
      if (patch.fontType !== undefined) mapped[`${base}FontType`] = patch.fontType;
      setBasic(mapped);
    },
  });

  const styleToggle = (row: 'title' | 'hint') => (
    <button
      type='button'
      className={[
        'form-builder-props__style-toggle',
        styleOpen === row && 'form-builder-props__style-toggle--active',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={t('forms_builder_props_style_toggle')}
      aria-pressed={styleOpen === row}
      onClick={() => setStyleOpen((prev) => (prev === row ? null : row))}
    >
      <Icon name='type' size={13} />
      Aa
    </button>
  );

  return (
    <section className='form-builder-props__accordion'>
      <button
        type='button'
        className='form-builder-props__accordion-head form-builder-props__accordion-head--brand'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('forms_builder_props_basic')}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </button>

      {open && (
        <div className='form-builder-props__accordion-body'>
          <div className='form-builder-props__field-row'>
            <span className='form-builder-props__label'>
              {t('forms_builder_props_title_label')}
              <span className='form-builder-props__label-note'>
                {' · '}
                {t(titleNoteKey)}
              </span>
            </span>
            {styleToggle('title')}
          </div>
          <Textarea
            rows={3}
            value={str(`${prefix}Title`)}
            onChange={(e) => setBasic({ [`${prefix}Title`]: e.target.value })}
          />
          {styleOpen === 'title' && <TextStyleEditor {...styleEditorProps(`${prefix}Title`)} />}

          {hasHint && (
            <>
              <div className='form-builder-props__field-row'>
                <span className='form-builder-props__label'>
                  {t('forms_builder_props_hint_label')}
                  <span className='form-builder-props__label-note'>
                    {' · '}
                    {t('form_settings_optional')}
                  </span>
                </span>
                {styleToggle('hint')}
              </div>
              <Input
                value={str(`${prefix}HintText`)}
                size='sm'
                onChange={(e) => setBasic({ [`${prefix}HintText`]: e.target.value })}
              />
              {styleOpen === 'hint' && (
                <TextStyleEditor {...styleEditorProps(`${prefix}HintText`)} />
              )}
            </>
          )}

          {field.elementType === 'checkList' && (
            <>
              <div className='form-builder-props__field-row'>
                <span className='form-builder-props__label'>
                  {t('forms_builder_props_choices')}
                </span>
                <span className='form-builder-props__label-note'>
                  {t('forms_builder_props_choices_note')}
                </span>
              </div>
              {/* <string> explicitly: the inline literals would narrow V to
                  'dropdown' | 'choiceList', which str()'s plain string is not
                  assignable to. */}
              <SelectDropdown<string>
                size='sm'
                aria-label={t('forms_builder_props_choices')}
                value={str('subType') || 'choiceList'}
                options={[
                  { value: 'dropdown', label: t('forms_builder_props_choices_dropdown') },
                  { value: 'choiceList', label: t('forms_builder_props_choices_list') },
                ]}
                onChange={(next) => setBasic({ subType: next })}
              />

              <div className='form-builder-props__toggle-card'>
                <span className='form-builder-props__toggle-icon'>
                  <Icon name='list-checks' size={18} />
                </span>
                <span className='form-builder-props__toggle-text'>
                  <span className='form-builder-props__toggle-title'>
                    {t('forms_builder_props_multiple')}
                  </span>
                  <span className='form-builder-props__toggle-desc'>
                    {t('forms_builder_props_multiple_desc')}
                  </span>
                </span>
                {/* Locked while conditional rules exist — the mutual pair of the
                 * IF button's own multipleChoice gate (legacy-identical). */}
                <Switch
                  checked={Boolean(bp.multipleChoice)}
                  disabled={rulesLocked}
                  title={rulesLocked ? t('forms_builder_logic_multiple_locked') : undefined}
                  aria-label={t('forms_builder_props_multiple')}
                  onChange={(e) => setBasic({ multipleChoice: e.target.checked })}
                />
              </div>

              <ChoiceOptionsEditor
                options={(bp.optionList as ChoiceOption[]) ?? []}
                onChange={(optionList) => setBasic({ optionList })}
                onApplyResponseSet={
                  onApplyResponseSet ?? ((optionList) => setBasic({ optionList }))
                }
              />
            </>
          )}

          {/* Placeholder sits with Title and Hint — the shared basic rows come
           * first, then the type-specific extras. */}
          {'fieldPlaceholder' in field.properties.basicProperties && (
            <>
              <div className='form-builder-props__field-row'>
                <span className='form-builder-props__label'>
                  {t('forms_builder_props_placeholder')}
                </span>
              </div>
              <Input
                value={str('fieldPlaceholder')}
                size='sm'
                onChange={(e) => setBasic({ fieldPlaceholder: e.target.value })}
              />
            </>
          )}

          {field.elementType !== 'page' && (
            <FormBuilderBasicExtras
              field={field as BuilderField}
              onChange={onChange}
              bindableFields={bindableFields}
            />
          )}

          {'fieldImage' in field.properties.basicProperties && (
            <>
              <div className='form-builder-props__field-row'>
                <span className='form-builder-props__label'>
                  {t('forms_builder_props_basic_image')}
                </span>
              </div>
              {fieldImageUrl ? (
                <div className='form-builder-props__basic-image-card'>
                  <img
                    className='form-builder-props__basic-image-preview'
                    src={fieldImageUrl}
                    alt={t('forms_builder_props_basic_image')}
                  />
                  <div className='form-builder-props__basic-image-bar'>
                    <span className='form-builder-props__basic-image-name'>
                      {bp.fieldImageFile instanceof File
                        ? bp.fieldImageFile.name
                        : t('forms_builder_props_basic_image')}
                    </span>
                    <IconButton
                      variant='ghost'
                      size='sm'
                      icon='refresh-cw'
                      aria-label={t('forms_builder_props_basic_image_replace')}
                      onClick={() => imageInputRef.current?.click()}
                    />
                    <IconButton
                      variant='ghost'
                      size='sm'
                      icon='trash-2'
                      className='form-builder-props__basic-image-remove'
                      aria-label={t('forms_builder_props_basic_image_remove')}
                      onClick={() => setBasic({ fieldImageFile: null, fieldImage: null })}
                    />
                  </div>
                </div>
              ) : (
                <button
                  type='button'
                  className='form-builder-props__basic-upload'
                  onClick={() => imageInputRef.current?.click()}
                >
                  <Icon name='upload' size={20} />
                  <span>{t('forms_builder_props_basic_image_upload')}</span>
                </button>
              )}
              <input
                ref={imageInputRef}
                type='file'
                accept='image/*'
                className='form-builder-props__basic-image-input'
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Stash the file only — the preview URL is derived from it at
                  // render time, and the upload happens on save
                  // (uploadPendingFieldImages), which then fills the descriptor.
                  if (file) setBasic({ fieldImageFile: file });
                  e.target.value = '';
                }}
              />
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default FormBuilderBasicOptions;
